import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { AchievementRarity } from '../../client/generated/graphql';
import { getAchievementRarityColor } from '../../helpers/achievement';
import { CommonIcon } from '../common/CommonIcon';

export interface AchievementItemProps {
  icon?: string | null;
  title?: string;
  timeAgo?: string;
  rarity: AchievementRarity;
  iconImageColor?: string | null;
  iconImageUrl?: string | null;
  onClick?: () => void;
}

export const AchievementItem: FC<AchievementItemProps> = ({ 
  icon, 
  title, 
  timeAgo,
  rarity,
  iconImageColor, 
  iconImageUrl,
  onClick
}) => { 
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxHeight, setBoxHeight] = useState(0);
  const bgColor = iconImageColor ? iconImageColor : getAchievementRarityColor(rarity);
  useEffect(() => {
    const updateHeight = () => {
      if (boxRef.current) {
        setBoxHeight(boxRef.current.offsetHeight);
      }
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);
  
  const iconSize = useMemo(() => Math.round(boxHeight * (title ? 0.4 : 0.5)), [boxHeight, title]);
  const titleFontSize = useMemo(() => Math.max(12, Math.round(boxHeight * 0.08)), [boxHeight]);
  const timeFontSize = useMemo(() => Math.max(9, Math.round(boxHeight * 0.06)), [boxHeight]);
  const timeIconSize = useMemo(() => Math.max(8, Math.round(boxHeight * 0.05)), [boxHeight]);
  const dynamicSpacing = useMemo(() => Math.max(0.5, boxHeight * 0.01), [boxHeight]);
  const dynamicPadding = useMemo(() => Math.max(4, boxHeight * 0.04), [boxHeight]);

  return (
    <Box ref={boxRef} onClick={onClick} sx={{
      backgroundColor: bgColor,
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      height: '100%',
      width: '100%',
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      padding: `${dynamicPadding}px`,
      paddingBottom: `${dynamicPadding+10}px`,
      boxSizing: 'border-box',
      borderBottomLeftRadius: '48px',
      borderBottomRightRadius: '48px',
      cursor: onClick ? 'pointer' : 'default',
    }}>
      <Stack
        direction="column"
        alignItems="center"
        justifyContent={title ? "flex-start" : "center"}
        height="100%"
        gap={dynamicSpacing}
      >
        {/* Icon or Image */}
        <Box sx={{ flexShrink: 0 }}>
          {iconImageUrl ? (
            <Box
              component="img"
              src={iconImageUrl}
              alt={title || 'Achievement'}
              sx={{
                width: `${iconSize}px`,
                height: `${iconSize}px`,
                borderRadius: '6px',
                objectFit: 'cover',
              }}
            />
          ) : (
            <CommonIcon
              icon={icon!}
              type="outlined"
              color="#ffffff"
              size={iconSize}
            />
          )}
        </Box>

        {/* Title */}
        {title && <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: 0, marginTop: '10px' }}>
          <Text
            weight="2"
            style={{
              color: '#ffffff',
              textAlign: 'center',
              whiteSpace: 'normal',
              fontSize: `${titleFontSize}px`,
              lineHeight: '1.2',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </Text>
        </Box>}

        {/* Time */}
        {timeAgo && <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ flexShrink: 0 }}
        >
          <CommonIcon 
            icon="calendar_today" 
            type="outlined" 
            color="#ffffff" 
            size={timeIconSize}
          />
          <Text style={{ 
            color: '#ffffff', 
            opacity: 0.8, 
            whiteSpace: 'nowrap', 
            fontSize: `${timeFontSize}px` 
          }}>
            {timeAgo}
          </Text>
        </Stack>}
      </Stack>
    </Box>
  );
};
